// CE Incident Viewer — per-site incident list with status management.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';

const STATUS_STYLES = {
  open:    'bg-amber-900/60 text-amber-300 border-amber-700',
  closed:  'bg-gray-700 text-gray-400 border-gray-600',
  escalated: 'bg-red-900/60 text-red-300 border-red-700',
};

function StaleBadge() {
  return (
    <span className="px-1.5 py-0.5 text-xs rounded border border-amber-700 bg-amber-900/50 text-amber-300 font-mono">
      Evidence Stale — Rebuild Required
    </span>
  );
}

function IncidentDetail({ incident, onClose, onUpdate }) {
  const [resolution, setResolution] = useState('');
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState(null);

  const handleClose = async () => {
    if (!resolution.trim()) { setErr('Resolution notes required to close.'); return; }
    setClosing(true);
    setErr(null);
    try {
      await api.put(`/ce/incidents/${incident.id}`, {
        status: 'closed',
        resolution_notes: resolution,
      });
      onUpdate();
      onClose();
    } catch (e) {
      setErr(errorDetail(e, 'Failed to close incident'));
    } finally {
      setClosing(false);
    }
  };

  const ep = incident.episode;

  return (
    <div className="mt-2 ml-6 mb-3 bg-gray-750 rounded-lg border border-gray-700 p-4">
      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div>
          <span className="text-gray-500">Type</span>
          <p className="text-gray-200 font-mono mt-0.5">{incident.incident_type}</p>
        </div>
        <div>
          <span className="text-gray-500">Opened</span>
          <p className="text-gray-200 mt-0.5">{new Date(incident.opened_at).toLocaleString()}</p>
        </div>
        {incident.closed_at && (
          <div>
            <span className="text-gray-500">Closed</span>
            <p className="text-gray-200 mt-0.5">{new Date(incident.closed_at).toLocaleString()}</p>
          </div>
        )}
        {incident.resolution_notes && (
          <div className="col-span-2">
            <span className="text-gray-500">Resolution</span>
            <p className="text-gray-300 mt-0.5">{incident.resolution_notes}</p>
          </div>
        )}
      </div>

      {ep && (
        <div className="border-t border-gray-700 pt-3 mb-1">
          <p className="text-xs text-gray-500 mb-2">Episode context — distinguishes this incident from others of the same type</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-gray-500">Trigger type</span>
              <p className="text-gray-200 font-mono mt-0.5">{ep.trigger_type || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500">Recurrence count</span>
              <p className="text-gray-200 mt-0.5">{ep.recurrence_count ?? '—'} seed events</p>
            </div>
            <div>
              <span className="text-gray-500">First seen</span>
              <p className="text-gray-200 mt-0.5">{ep.first_seen ? new Date(ep.first_seen).toLocaleString() : '—'}</p>
            </div>
            <div>
              <span className="text-gray-500">Last seen</span>
              <p className="text-gray-200 mt-0.5">{ep.last_seen ? new Date(ep.last_seen).toLocaleString() : '—'}</p>
            </div>
            {ep.protocols && ep.protocols.length > 0 && (
              <div className="col-span-2">
                <span className="text-gray-500">Protocols</span>
                <p className="text-gray-200 mt-0.5 font-mono">{ep.protocols.join(', ')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {incident.status === 'open' && (
        <div className="border-t border-gray-700 pt-3 mt-1">
          <label className="block text-xs text-gray-400 mb-1">Resolution notes</label>
          <textarea
            rows={2}
            value={resolution}
            onChange={e => { setResolution(e.target.value); setErr(null); }}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Describe the resolution..."
          />
          {err && <p className="text-xs text-red-400 mt-1">{err}</p>}
          <button
            onClick={handleClose}
            disabled={closing}
            className="mt-2 px-4 py-1.5 text-xs rounded bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-gray-200 transition-colors"
          >
            {closing ? 'Closing...' : 'Close Incident'}
          </button>
        </div>
      )}
    </div>
  );
}

function IncidentRow({ incident, expanded, onToggle, onUpdate }) {
  const statusCls = STATUS_STYLES[incident.status] || 'bg-gray-700 text-gray-400 border-gray-600';
  return (
    <div className="border-b border-gray-700/60 last:border-0">
      <button
        className="w-full flex items-start gap-3 py-3 px-1 text-left hover:bg-gray-750/30 transition-colors"
        onClick={() => onToggle(incident.id)}
      >
        <span className="text-gray-500 text-xs mt-1 w-4 shrink-0">{expanded ? '▼' : '▶'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className={`px-1.5 py-0.5 text-xs rounded border font-mono ${statusCls}`}>
              {incident.status}
            </span>
            <span className="text-sm font-medium text-gray-100">
              {incident.display_label || incident.incident_type}
            </span>
            {incident.stale && <StaleBadge />}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            <span className="font-mono">{incident.id}</span>
            <span>{new Date(incident.opened_at).toLocaleString()}</span>
            {incident.episode?.recurrence_count != null && (
              <span>{incident.episode.recurrence_count} seed events</span>
            )}
          </div>
        </div>
      </button>
      {expanded && (
        <IncidentDetail
          incident={incident}
          onClose={() => onToggle(incident.id)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

export default function CEIncidentViewer() {
  const [searchParams] = useSearchParams();
  const initSite = searchParams.get('site') || '';

  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(initSite);
  const [statusFilter, setStatusFilter] = useState('open');
  const [incidents, setIncidents] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/ce/sites').then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const loadIncidents = useCallback(async () => {
    if (!siteId) { setIncidents([]); return; }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 50, status: statusFilter });
      const { data } = await api.get(`/ce/sites/${siteId}/incidents?${params}`);
      setIncidents(data.incidents || []);
      setExpandedIds(new Set());
    } catch (err) {
      setError(errorDetail(err, 'Failed to load incidents'));
    } finally {
      setLoading(false);
    }
  }, [siteId, statusFilter]);

  useEffect(() => { loadIncidents(); }, [loadIncidents]);

  const handleToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }, []);

  const openCount = incidents.filter(i => i.status === 'open').length;
  const siteName = sites.find(s => s.id === siteId)?.site_name || siteId;

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-1">Incident Viewer</h1>
        <p className="text-sm text-gray-400 mb-6">Compliance incidents derived from anomalous event sequences</p>

        {/* Filters */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Site</label>
            <select
              value={siteId}
              onChange={e => setSiteId(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select site</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.site_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="open">Open only</option>
              <option value="all">All</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <button
            onClick={loadIncidents}
            disabled={loading || !siteId}
            className="px-4 py-2 text-sm rounded-lg bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white transition-colors"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        </div>

        {!siteId && (
          <p className="text-sm text-gray-500 text-center py-12">Select a site to view incidents.</p>
        )}

        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300 mb-4">
            {error}
          </div>
        )}

        {siteId && !loading && incidents.length === 0 && !error && (
          <p className="text-sm text-gray-500 text-center py-12">
            No {statusFilter === 'all' ? '' : statusFilter + ' '}incidents for {siteName}.
          </p>
        )}

        {incidents.length > 0 && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 px-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-700">
              <span className="text-sm font-medium text-gray-300">
                {incidents.length} incident{incidents.length !== 1 ? 's' : ''} — {siteName}
              </span>
              {openCount > 0 && (
                <span className="px-2 py-0.5 text-xs rounded bg-amber-900/60 text-amber-300 border border-amber-700">
                  {openCount} open
                </span>
              )}
            </div>
            {incidents.map(incident => (
              <IncidentRow
                key={incident.id}
                incident={incident}
                expanded={expandedIds.has(incident.id)}
                onToggle={handleToggle}
                onUpdate={loadIncidents}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
